import { getAquilaApiClient } from "../api/aquilaApi/aquilaApi";
import {
    getBooleanFromString,
    getValueFromSearchParams,
    getWindowValue,
    WindowProperty,
} from "../utils/window.utils";

import { authStore } from "./auth";
import type { UserPreference } from "./userPreferences";
import { UserPreferenceData, UserPreferencesStore } from "./userPreferences";
import { zustandStore } from "./zustand.helpers";

export enum FeatureFlags {
    UAT_PACKAGED_INSIGHT = "uatPackagedInsights",
    UAT_PEER_BENCHMARKING = "uatPeerBenchmarking",
    UAT_STANDALONE = "uatStandalone",
    SUPPORT_MODE = "supportMode",
    ALL_FEATURE_FLAGS_DROP_DOWN = "allFeatureFlagsDropDown",
    FORGE_VIZ = "forgeViz",
    THUMBS_FEEDBACK_ON_CARD = "thumbsFeedbackCard",
    ENABLE_TEST_ACCOUNTS = "enableTestAccounts",
    SHOW_CAIRE_IN_DROPDOWN = "showCaireRecsInDropdown",

    CLOUDWATCH_RUM = "cloudwatchRUM",
    PHOENIX_TRACING = "phoenixTracing",
    FORGE_OPTIN_TOGGLE = "forgeOptinToggle",
    UX_REVAMP = "uxRevamp",
    FORGE_MFC = "forgeMFC",
    AI_SEARCH = "aiSearch",
}

export type FeatureFlagsState = {
    [flag in FeatureFlags]: boolean;
};

export const initialState: FeatureFlagsState = {
    [FeatureFlags.FORGE_VIZ]: false,
    [FeatureFlags.THUMBS_FEEDBACK_ON_CARD]: false,

    [FeatureFlags.CLOUDWATCH_RUM]: false,
    [FeatureFlags.FORGE_OPTIN_TOGGLE]: false,
    [FeatureFlags.UX_REVAMP]: false,
    [FeatureFlags.FORGE_MFC]: false,
    [FeatureFlags.PHOENIX_TRACING]: false,
    [FeatureFlags.SHOW_CAIRE_IN_DROPDOWN]: false,
    [FeatureFlags.AI_SEARCH]: false,

    // Always here
    [FeatureFlags.UAT_PACKAGED_INSIGHT]: false,
    [FeatureFlags.UAT_PEER_BENCHMARKING]: false,
    [FeatureFlags.UAT_STANDALONE]: false,
    [FeatureFlags.SUPPORT_MODE]: false,
    [FeatureFlags.ALL_FEATURE_FLAGS_DROP_DOWN]: false,
    [FeatureFlags.ENABLE_TEST_ACCOUNTS]: false,
};

export const featureFlagLabelMap: {
    [flag in FeatureFlags]: string;
} = {
    [FeatureFlags.FORGE_VIZ]: "Forge Visualization",
    [FeatureFlags.THUMBS_FEEDBACK_ON_CARD]: "Thumbs Feedback on Card",
    [FeatureFlags.CLOUDWATCH_RUM]: "CloudWatch RUM",
    [FeatureFlags.FORGE_MFC]: "Forge MFC",
    [FeatureFlags.PHOENIX_TRACING]: "Phoenix Tracing",
    [FeatureFlags.SHOW_CAIRE_IN_DROPDOWN]:
        "Show CAIRE Recommendations in type dropdown",
    [FeatureFlags.AI_SEARCH]: "AI Search",

    // Always here
    [FeatureFlags.UAT_PACKAGED_INSIGHT]: "UAT Packaged Insight",
    [FeatureFlags.UAT_PEER_BENCHMARKING]: "UAT Peer Benchmarking",
    [FeatureFlags.UAT_STANDALONE]: "UAT Standalone Families",
    [FeatureFlags.SUPPORT_MODE]: "Support Mode",
    [FeatureFlags.ALL_FEATURE_FLAGS_DROP_DOWN]: "All Feature Flags Drop Down",
    [FeatureFlags.ENABLE_TEST_ACCOUNTS]: "ENABLE_TEST_ACCOUNTS",

    [FeatureFlags.FORGE_OPTIN_TOGGLE]: "Forge Opt-in Toggle",
    [FeatureFlags.UX_REVAMP]: "UX revamp",
};

export const READY_TO_UAT_FEATURES: FeatureFlags[] = [
    FeatureFlags.UAT_PACKAGED_INSIGHT,
    FeatureFlags.UAT_STANDALONE,
];

export interface FeatureFlagsActions {
    setFeatureFlags: (flags: Partial<FeatureFlagsState>) => void;
    resetFeatureFlags: () => void;
}

export const { store: FeatureFlagsStore } = zustandStore<
    FeatureFlagsState,
    FeatureFlagsActions
>("FeatureFlags", initialState, (set: any) => ({
    setFeatureFlags: (flags: Partial<FeatureFlagsState>) =>
        set(() => ({ ...flags })),
    resetFeatureFlags: () => set(() => ({ ...initialState })),
}));

const getAquilaFlags = async (
    requesterAlias: string,
): Promise<{ [key: string]: boolean }> => {
    try {
        const {
            settings: { aquilaConfig },
        } = authStore.getState();
        const aquilaFlags = await (
            await getAquilaApiClient()
        ).getAccessByAppConfig({
            requesterAlias,
            ...aquilaConfig.featureAccess,
        });

        return aquilaFlags.data
            .filter((f) => f.allowed)
            .reduce<{ [key: string]: boolean }>((obj, flag) => {
                return { ...obj, [flag.name!]: true };
            }, {});
    } catch (e: any) {
        return {};
    }
};

const isFeatureEnabledWithPrecedence = (
    flag: string,
    aquilaFlags: { [key: string]: boolean },
): boolean => {
    // First precedence set for value set from URL param
    const urlParamValue = getValueFromSearchParams(flag);
    if (urlParamValue !== undefined) {
        return getBooleanFromString(urlParamValue);
    }

    // Second precedence set from user preferences
    const userPreferenceKey = (
        Object.keys(UserPreferenceData) as UserPreference[]
    ).find((key) => UserPreferenceData[key].featureFlagControl === flag);
    if (userPreferenceKey) {
        // Explicitely pull from the state instead of getPreference() so we avoid an infinite loop
        const preference =
            UserPreferencesStore.getState().preferences[userPreferenceKey];
        if (preference !== undefined) {
            // Returns false for non-boolean values
            return getBooleanFromString(preference);
        }
    }

    // Third precedence set for value set from aquila
    if (flag in aquilaFlags) {
        return aquilaFlags[flag];
    }

    // Fourth precedence set from weblab
    const weblabValue = getWindowValue(
        `${WindowProperty.IHUB_STATE}.weblabs.${flag}`,
    );
    if (weblabValue !== undefined) {
        return !!weblabValue;
    }

    // Fifth precedence set from initial state
    return initialState[flag] ?? false;
};

export const getFlagValues = async (
    requesterAlias: string,
): Promise<FeatureFlagsState> => {
    const aquilaFlags = await getAquilaFlags(requesterAlias);
    return Object.keys(initialState).reduce((obj, flag) => {
        if (flag in obj) {
            return {
                ...obj,
                [flag]: isFeatureEnabledWithPrecedence(flag, aquilaFlags),
            };
        }
        return {
            ...obj,
        };
    }, initialState);
};
